/**
 * A safety valve's set pressure is one stored string, `<number> <unit><modifier>`:
 * `9.4 kg/cm2g`, `1.2 bara`, `350 mm H2O`. This module holds that grammar.
 */

/*
 * DECISION(2026-10-26, abhishek): The unit list kg/cm2, bar, mm H2O and the modifier list g, a, d are the grammar of the annotate-safety-valves skill's canonical set-pressure table; a change to one changes the other.
 * The skill writes the stored string from drawings, so the editor must parse every string
 * the skill writes, and the SME must see any stored value outside the grammar as read-only text.
 */
export const SET_PRESSURE_UNITS = ['kg/cm2', 'bar', 'mm H2O'] as const;
export const SET_PRESSURE_MODIFIERS = ['g', 'a', 'd'] as const;

export type SetPressureUnit = (typeof SET_PRESSURE_UNITS)[number];
/** The empty string is "no modifier": the stored string ends at the unit. */
export type SetPressureModifier = (typeof SET_PRESSURE_MODIFIERS)[number] | '';

export interface SetPressureParts {
  /** The number as typed, e.g. `-0.5`. */
  value: string;
  unit: SetPressureUnit;
  modifier: SetPressureModifier;
}

const SET_PRESSURE_NUMBER = /^-?\d+(\.\d+)?$/;
const CANONICAL_SET_PRESSURE = new RegExp(
  `^(-?\\d+(?:\\.\\d+)?) (${SET_PRESSURE_UNITS.join('|')})(${SET_PRESSURE_MODIFIERS.join('|')})?$`,
);

export function isSetPressureNumber(text: string): boolean {
  return SET_PRESSURE_NUMBER.test(text);
}

/** The parts of a stored string, or null when it is outside the grammar. */
export function parseSetPressure(stored: string): SetPressureParts | null {
  const match = CANONICAL_SET_PRESSURE.exec(stored);
  if (!match) return null;
  return {
    value: match[1],
    unit: match[2] as SetPressureUnit,
    modifier: (match[3] ?? '') as SetPressureModifier,
  };
}

export function formatSetPressure(parts: SetPressureParts): string {
  return `${parts.value} ${parts.unit}${parts.modifier}`;
}
